// client/src/components/Banner.js
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Stack, Typography, Container, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useStore } from '../context/StoreContext';
import { useCategories } from '../context/CategoryContext';
import { API_BASE_URL, getImageUrl } from '../utils/media';

const ROTATE_INTERVAL_MS = 5000;
const BANNER_HEIGHT = { xs: 320, sm: 420, md: 520 };

// Shared with the footer and promo strip, so dark sections match across the page.
export const DARK_SECTION_COLOR = '#0B0D12';

const FLIGHT_PATH = 'M 40 340 C 200 340, 250 120, 420 150 S 600 310, 720 60';
// A 24 x 24 plane icon, scaled up and centered on its own position.
const PLANE_ICON = (
  <path
    transform="scale(2.2) translate(-12 -12)"
    fill="#fff"
    d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"
  />
);
// Curves the bottom of the hero into the page color below it.
const HeroCurve = () => (
  <Box
    component="svg"
    viewBox="0 0 1440 56"
    preserveAspectRatio="none"
    aria-hidden
    sx={{
      position: 'absolute', left: 0, right: 0, bottom: -1, display: 'block', width: '100%',
      height: { xs: 24, md: 56 }, color: 'background.default', pointerEvents: 'none', zIndex: 1,
    }}
  >
    <path fill="currentColor" d="M0 56V28C240 4 480 0 720 12s480 28 720 8v36z" />
  </Box>
);

// A dashed flight path with a plane flying along it, on larger screens only.
const FlightGraphic = () => (
  <Box
    aria-hidden
    sx={{
      display: { xs: 'none', md: 'block' }, position: 'absolute', top: '50%', right: { md: '-4%', lg: '2%' },
      width: { md: 620, lg: 760 }, height: { md: 326, lg: 400 }, transform: 'translateY(-50%)', pointerEvents: 'none',
    }}
  >
    <Box component="svg" viewBox="0 0 760 400" sx={{ width: '100%', height: '100%', overflow: 'visible' }}>
      <circle cx="420" cy="150" r="72" fill="none" stroke="rgba(255,255,255,0.1)" />
      <circle cx="420" cy="150" r="150" fill="none" stroke="rgba(255,255,255,0.07)" />
      <circle cx="420" cy="150" r="240" fill="none" stroke="rgba(255,255,255,0.04)" />
      <path d={FLIGHT_PATH} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="4 12" strokeLinecap="round" />
      <circle cx="40" cy="340" r="5" fill="#fff" />
      {window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? (
        <g transform="translate(565 214) rotate(35)">{PLANE_ICON}</g>
      ) : (
        <g opacity="0">
          <animateMotion dur="11s" repeatCount="indefinite" rotate="auto" path={FLIGHT_PATH} calcMode="spline" keyTimes="0;1" keySplines="0.45 0 0.55 1" />
          <animate attributeName="opacity" dur="11s" repeatCount="indefinite" keyTimes="0;0.08;0.92;1" values="0;1;1;0" />
          <g transform="rotate(90)">{PLANE_ICON}</g>
        </g>
      )}
    </Box>
  </Box>
);

const DefaultHero = () => {
  const { categories } = useCategories();
  const hasPlanes = categories.some((category) => category.name === 'RC Planes');

  return (
    <Box
      sx={{
        position: 'relative', overflow: 'hidden', color: '#fff', bgcolor: DARK_SECTION_COLOR,
        pt: { xs: 7, md: 12 }, pb: { xs: 10, md: 16 },
        backgroundImage: (theme) => [
          `radial-gradient(820px 520px at 78% 22%, ${alpha(theme.palette.primary.main, 0.75)}, transparent 70%)`,
          `radial-gradient(620px 420px at 8% 100%, ${alpha(theme.palette.secondary.main, 0.32)}, transparent 70%)`,
        ].join(','),
      }}
    >
      <FlightGraphic />
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="overline" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)', letterSpacing: '0.18em', fontWeight: 700, mb: 1.5 }}>
          For pilots and model builders
        </Typography>
        <Typography
          component="h1"
          sx={{ fontSize: { xs: '2.75rem', sm: '3.75rem', md: '5rem' }, fontWeight: 800, lineHeight: 1.02, letterSpacing: '-0.035em', maxWidth: 720, mb: 2.5 }}
        >
          Your next build starts here.
        </Typography>
        <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, color: 'rgba(255,255,255,0.8)', maxWidth: 560, mb: 4 }}>
          RC planes, helicopters and model kits, with the parts to keep them flying.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ maxWidth: { xs: 320, sm: 'none' } }}>
          <Button
            component={RouterLink}
            to={hasPlanes ? `/?${new URLSearchParams({ category: 'RC Planes' })}` : '/?sort=newest'}
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{ bgcolor: '#fff', color: '#0B0D12', px: 3.5, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.86)' } }}
          >
            {hasPlanes ? 'Shop RC planes' : 'Browse new arrivals'}
          </Button>
          {hasPlanes && (
            <Button
              component={RouterLink}
              to="/?sort=newest"
              size="large"
              variant="outlined"
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.45)', px: 3.5, py: 1.25, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              New arrivals
            </Button>
          )}
        </Stack>
      </Container>
      <HeroCurve />
    </Box>
  );
};

// A banner slide's link_url can be an internal path (e.g. "/?category=RC Planes")
// or a full external URL - internal paths use client-side routing (no full
// page reload), external ones are a plain anchor tag.
const SlideLink = ({ linkUrl, children }) => {
  if (!linkUrl) return children;
  const isExternal = /^https?:\/\//i.test(linkUrl);
  if (isExternal) {
    return <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{children}</a>;
  }
  return <RouterLink to={linkUrl} style={{ display: 'block' }}>{children}</RouterLink>;
};

const Banner = () => {
  const { store, loaded: storeLoaded } = useStore();
  const [slides, setSlides] = useState([]);
  const [slidesLoaded, setSlidesLoaded] = useState(false);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!store.id) return;
    fetch(`${API_BASE_URL}/api/store/banners`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setSlides)
      .catch(() => setSlides([]))
      .finally(() => setSlidesLoaded(true));
  }, [store.id]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goTo = useCallback((index) => {
    setCurrent((index + slides.length) % slides.length);
  }, [slides.length]);

  // Holds the space while slides load, so the default hero doesn't flash first.
  if (!slidesLoaded && !(storeLoaded && !store.id)) {
    return <Box sx={{ height: BANNER_HEIGHT, bgcolor: DARK_SECTION_COLOR }} />;
  }

  if (slides.length === 0) return <DefaultHero />;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: BANNER_HEIGHT,
        overflow: 'hidden',
        bgcolor: DARK_SECTION_COLOR,
      }}
    >
      {slides.map((slide, index) => (
        <Box
          key={slide.id}
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: index === current ? 1 : 0,
            transition: 'opacity 0.6s ease-in-out',
            pointerEvents: index === current ? 'auto' : 'none',
          }}
        >
          <SlideLink linkUrl={slide.link_url}>
            <Box
              component="img"
              src={getImageUrl(slide.image_url)}
              alt={`${store.name} banner ${index + 1}`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </SlideLink>
        </Box>
      ))}

      {slides.length > 1 && (
        <>
          <IconButton
            onClick={() => goTo(current - 1)}
            aria-label="Previous banner"
            sx={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
              bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => goTo(current + 1)}
            aria-label="Next banner"
            sx={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
              bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          <Box sx={{ position: 'absolute', bottom: { xs: 32, md: 64 }, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1, zIndex: 2 }}>
            {slides.map((slide, index) => (
              <Box
                key={slide.id}
                component="button"
                onClick={() => goTo(index)}
                aria-label={`Go to banner ${index + 1}`}
                sx={{
                  width: 9, height: 9, borderRadius: '50%', border: 'none', p: 0, cursor: 'pointer',
                  bgcolor: index === current ? '#fff' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </Box>
        </>
      )}
      <HeroCurve />
    </Box>
  );
};

export default Banner;
